"use client";

import { useState, useTransition } from "react";
import { track } from "@/lib/analytics";

interface SubmissionFormProps {
  categories: { name: string; slug: string }[];
}

interface FormErrors {
  siteName?: string;
  url?: string;
  description?: string;
}

function validateForm(data: {
  siteName: string;
  url: string;
  description: string;
}): FormErrors {
  const errors: FormErrors = {};

  if (!data.siteName.trim()) {
    errors.siteName = "Site name is required.";
  }

  if (!data.url.trim()) {
    errors.url = "URL is required.";
  } else {
    try {
      new URL(data.url);
    } catch {
      errors.url = "Please enter a valid URL (e.g., https://example.com).";
    }
  }

  if (!data.description.trim()) {
    errors.description = "Description is required.";
  } else if (data.description.trim().length < 10) {
    errors.description = "Description must be at least 10 characters.";
  }

  return errors;
}

export default function SubmissionForm({ categories }: SubmissionFormProps) {
  const [isPending, startTransition] = useTransition();
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverError, setServerError] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});

  const [siteName, setSiteName] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [categorySlug, setCategorySlug] = useState("");
  const [email, setEmail] = useState("");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError("");

    const validationErrors = validateForm({ siteName, url, description });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    startTransition(async () => {
      try {
        const response = await fetch("/api/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            siteName: siteName.trim(),
            url: url.trim(),
            description: description.trim(),
            categorySlug: categorySlug || null,
            email: email.trim() || null,
          }),
        });

        if (!response.ok) {
          const data = (await response.json().catch(() => null)) as {
            error?: string;
          } | null;
          throw new Error(
            data?.error ?? "Something went wrong. Please try again."
          );
        }

        // GA4 conversion: user submitted a tool. Fire before clearing fields.
        track("tool_submit", { category: categorySlug || null });

        setIsSuccess(true);
        setSiteName("");
        setUrl("");
        setDescription("");
        setCategorySlug("");
        setEmail("");
      } catch (err) {
        setServerError(
          err instanceof Error
            ? err.message
            : "Something went wrong. Please try again."
        );
      }
    });
  };

  if (isSuccess) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 dark:bg-green-950/40 p-8 text-center">
        <div className="text-4xl" aria-hidden="true">
          ✓
        </div>
        <h3 className="mt-4 text-lg font-semibold text-green-800">
          Submission received!
        </h3>
        <p className="mt-2 text-sm text-green-600">
          Thank you for your submission. We will review it and add it to the
          directory if it meets our criteria.
        </p>
        <button
          onClick={() => setIsSuccess(false)}
          className="mt-6 inline-flex items-center rounded-lg border border-green-300 dark:border-green-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-green-700 dark:text-green-300 transition-colors hover:bg-green-50"
        >
          Submit another tool
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {serverError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {serverError}
        </div>
      )}

      {/* Site Name */}
      <div>
        <label
          htmlFor="siteName"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Site Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="siteName"
          value={siteName}
          onChange={(e) => {
            setSiteName(e.target.value);
            if (errors.siteName) setErrors((prev) => ({ ...prev, siteName: undefined }));
          }}
          placeholder="e.g., My Awesome Tool"
          className={`mt-1 block w-full rounded-lg border px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none transition-colors focus:ring-2 ${
            errors.siteName
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : "border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-blue-500/20"
          }`}
          required
        />
        {errors.siteName && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.siteName}</p>
        )}
      </div>

      {/* URL */}
      <div>
        <label
          htmlFor="url"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          URL <span className="text-red-500">*</span>
        </label>
        <input
          type="url"
          id="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (errors.url) setErrors((prev) => ({ ...prev, url: undefined }));
          }}
          placeholder="https://example.com"
          className={`mt-1 block w-full rounded-lg border px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none transition-colors focus:ring-2 ${
            errors.url
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : "border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-blue-500/20"
          }`}
          required
        />
        {errors.url && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.url}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            if (errors.description)
              setErrors((prev) => ({ ...prev, description: undefined }));
          }}
          placeholder="Briefly describe what this tool does and why it's useful..."
          rows={4}
          className={`mt-1 block w-full resize-y rounded-lg border px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none transition-colors focus:ring-2 ${
            errors.description
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : "border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-blue-500/20"
          }`}
          required
        />
        {errors.description && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.description}</p>
        )}
      </div>

      {/* Category */}
      <div>
        <label
          htmlFor="categorySlug"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Category
        </label>
        <select
          id="categorySlug"
          value={categorySlug}
          onChange={(e) => setCategorySlug(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        >
          <option value="">Select a category (optional)</option>
          {categories.map((cat) => (
            <option key={cat.slug} value={cat.slug}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Email <span className="text-xs text-gray-500 dark:text-gray-400">(optional)</span>
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="mt-1 block w-full rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          We will only use this to notify you when your submission is approved.
        </p>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isPending}
        className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isPending ? (
          <>
            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            Submitting...
          </>
        ) : (
          "Submit Tool"
        )}
      </button>
    </form>
  );
}
